import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { DirectMessageNavigationProvider, DirectMessagePropsProvider } from 'src/components/DirectMessage/context';
import DirectMessageLobby from 'src/components/DirectMessage/Lobby';
import DirectMessageProfile from 'src/components/DirectMessage/Profile';
import DirectMessageRoom from 'src/components/DirectMessage/Room';
import FollowBase from 'src/components/Follow';
import { FollowViewProvider } from 'src/components/Follow/context';
import Footer from 'src/components/Footer';
import Header from 'src/components/Header';
import { HEADER_HEIGHT } from 'src/components/Header/styles';
import withPrivateRoute from 'src/components/HOC/withPrivateRoute';
import withWebClientValidation from 'src/components/HOC/withWebClientValidation';
import { useFollowersQuery, useFollowingsQuery } from 'src/hooks/useFollowQuery';
import useQuickSlotPage from 'src/hooks/useQuickSlotPage';
import useStartMatch from 'src/hooks/useStartMatch';
import useUseDMFirstTime from 'src/hooks/useUseDMFirstTime';
import { screenWidth } from 'src/styles/screenSize';
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: var(--100vw);

  padding-top: ${HEADER_HEIGHT};
`;
const Container = styled.div`
  margin: 0 auto;

  --height: calc(var(--100vh, 100dvh) - ${HEADER_HEIGHT});
  --pad: 36px;

  padding-bottom: var(--pad);

  width: 100%;
  max-width: ${screenWidth.tablet - 16 * 2}px;
  min-height: var(--height);

  display: flex;
  gap: 24px;
`;
const leftViewStyles = ({
  theme
}: {
  theme: Theme;
}) => css`
  flex: 0 0 382px;
  background-color: ${theme.color.bg2};
  border-radius: 16px;

  max-height: calc(var(--height) - var(--pad));
  overflow-y: scroll;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const LobbyBase = styled(DirectMessageLobby)`
  height: 100%;
  background-color: ${({
  theme
}) => theme.color.bg2};
  border-radius: 16px;

  max-height: calc(var(--height) - var(--pad));
  overflow-y: scroll;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const LobbyWrapper = styled.div`
  position: relative;
  flex: 0 0 382px;
  min-width: 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(180deg, transparent 0%, ${({
  theme
}) => theme.color.bg2} 100%);
    pointer-events: none;
    border-radius: 0 0 16px 16px;
  }
`;
const Lobby = ({
  className,
  ...props
}: {
  className?: string;
}) => <LobbyWrapper className={className} data-sentry-element="LobbyWrapper" data-sentry-component="Lobby" data-sentry-source-file="MessagesDesktop.tsx">
    <LobbyBase {...props} data-sentry-element="LobbyBase" data-sentry-source-file="MessagesDesktop.tsx" />
  </LobbyWrapper>;
const Follow = styled(FollowBase)`
  ${leftViewStyles}

  padding: 24px 8px;
`;
const Room = styled(DirectMessageRoom)`
  flex: 1;

  background-color: ${({
  theme
}) => theme.color.bg2};
  border-radius: 16px;

  max-height: calc(var(--height) - var(--pad));
  padding: 24px 16px;
`;
const RoomProfileThumbnail = styled(DirectMessageProfile.Thumbnail)`
  width: 36px;

  margin-inline-end: 8px;
`;
const RoomProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  margin-inline-end: auto;
`;
const RoomSimpleName = styled(DirectMessageProfile.SimpleName)`
  font-weight: 400;
`;
type Mode = 'lobby' | 'follow';
type FollowTab = 'following' | 'follower';
const MessagesDesktopView = () => {
  const router = useRouter();
  const messageThreadId = router.query.messageThreadId ? String(router.query.messageThreadId) : null;
  const followTabFromUrl = router.query.followTab;
  const followTab: FollowTab = followTabFromUrl === 'follower' ? 'follower' : 'following';
  const mode: Mode = followTabFromUrl === 'following' || followTabFromUrl === 'follower' ? 'follow' : 'lobby';
  const {
    startMatch: openMatch
  } = useStartMatch();
  const openRoom = (threadId: string) => {
    router.push({
      query: {
        ...router.query,
        messageThreadId: threadId
      }
    }, undefined, {
      shallow: true
    });
  };
  const openFollowing = () => {
    router.push({
      query: {
        ...router.query,
        followTab: 'following'
      }
    }, undefined, {
      shallow: true
    });
  };
  const openFollower = () => {
    router.push({
      query: {
        ...router.query,
        followTab: 'follower'
      }
    }, undefined, {
      shallow: true
    });
  };
  const closeRoom = () => {
    const {
      messageThreadId: _,
      ...rest
    } = router.query;
    router.push({
      query: rest
    }, undefined, {
      shallow: true
    });
  };
  const navigateToLobby = () => {
    const {
      followTab: _,
      ...rest
    } = router.query;
    router.push({
      query: rest
    }, undefined, {
      shallow: true
    });
  };
  const handleTabChange = (tabId: string) => {
    router.push({
      query: {
        ...router.query,
        followTab: tabId
      }
    }, undefined, {
      shallow: true
    });
  };
  useQuickSlotPage('FRIEND_ADD_REQUEST');
  const {
    isLoading: isLobbyLoading
  } = useUseDMFirstTime();
  const isFollowMode = mode === 'follow';
  const {
    isLoading: isFollowingLoading
  } = useFollowingsQuery('ALL', {
    enabled: isFollowMode
  });
  const {
    isLoading: isFollowerLoading
  } = useFollowersQuery('ALL', {
    enabled: isFollowMode
  });
  const isLeftLoading = mode === 'lobby' ? isLobbyLoading : isFollowingLoading || isFollowerLoading;
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(() => !isLeftLoading);
  useEffect(() => {
    if (!isLeftLoading && !hasCompletedInitialLoad) {
      setHasCompletedInitialLoad(true);
    }
  }, [isLeftLoading, hasCompletedInitialLoad]);
  const isLeftInitialLoading = !hasCompletedInitialLoad;
  const Left = useMemo(() => {
    switch (mode) {
      case 'lobby':
        return Lobby;
      case 'follow':
        return Follow;
    }
  }, [mode]);
  return <DirectMessageNavigationProvider openMatch={openMatch} openRoom={openRoom} openFollowing={openFollowing} closeRoom={closeRoom} openFollower={openFollower} data-sentry-element="DirectMessageNavigationProvider" data-sentry-component="MessagesDesktopView" data-sentry-source-file="MessagesDesktop.tsx">
      <DirectMessagePropsProvider messageThreadId={messageThreadId} data-sentry-element="DirectMessagePropsProvider" data-sentry-source-file="MessagesDesktop.tsx">
        <FollowViewProvider currentTab={followTab} onNavigateBack={navigateToLobby} openChat={openRoom} onTabChange={handleTabChange} data-sentry-element="FollowViewProvider" data-sentry-source-file="MessagesDesktop.tsx">
          <Header data-sentry-element="Header" data-sentry-source-file="MessagesDesktop.tsx" />
          <Wrapper data-sentry-element="Wrapper" data-sentry-source-file="MessagesDesktop.tsx">
            <Container data-sentry-element="Container" data-sentry-source-file="MessagesDesktop.tsx">
              <Left data-sentry-element="Left" data-sentry-source-file="MessagesDesktop.tsx" />
              <Room isLeftInitialLoading={isLeftInitialLoading} data-sentry-element="Room" data-sentry-source-file="MessagesDesktop.tsx">
                <DirectMessageProfile data-sentry-element="DirectMessageProfile" data-sentry-source-file="MessagesDesktop.tsx">
                  <RoomProfileThumbnail data-sentry-element="RoomProfileThumbnail" data-sentry-source-file="MessagesDesktop.tsx" />
                  <RoomProfileInfo data-sentry-element="RoomProfileInfo" data-sentry-source-file="MessagesDesktop.tsx">
                    <RoomSimpleName data-sentry-element="RoomSimpleName" data-sentry-source-file="MessagesDesktop.tsx" />
                    <DirectMessageProfile.PeerTime data-sentry-element="DirectMessageProfile.PeerTime" data-sentry-source-file="MessagesDesktop.tsx" />
                  </RoomProfileInfo>
                </DirectMessageProfile>
                <DirectMessageRoom.MessageList data-sentry-element="DirectMessageRoom.MessageList" data-sentry-source-file="MessagesDesktop.tsx" />
                <DirectMessageRoom.Toolbar data-sentry-element="DirectMessageRoom.Toolbar" data-sentry-source-file="MessagesDesktop.tsx" />
              </Room>
            </Container>
            <Footer data-sentry-element="Footer" data-sentry-source-file="MessagesDesktop.tsx" />
          </Wrapper>
        </FollowViewProvider>
      </DirectMessagePropsProvider>
    </DirectMessageNavigationProvider>;
};
export default withWebClientValidation(withPrivateRoute(MessagesDesktopView));